import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import type { AdminVoicePhoneNumber } from "../../api/admin";
import { useAdminVoicePhoneNumberHistory } from "../../hooks/useVoicePhoneNumbers";

interface VoiceNumberDetailDrawerProps {
  number: AdminVoicePhoneNumber | null;
  onClose: () => void;
}

function formatDate(value: string | null | undefined) {
  if (!value) return "none";
  return new Date(value).toLocaleString();
}

function row(label: string, value: string | null | undefined) {
  return (
    <Box sx={{ display: "flex", gap: 2 }}>
      <Typography sx={{ minWidth: 150, fontSize: "0.76rem", color: "#94A3B8" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.78rem", color: "#475569", wordBreak: "break-all" }}>
        {value || "not set"}
      </Typography>
    </Box>
  );
}

export default function VoiceNumberDetailDrawer({
  number,
  onClose,
}: VoiceNumberDetailDrawerProps) {
  const { data: history, isLoading } = useAdminVoicePhoneNumberHistory(
    number?.voice_phone_number_id ?? null
  );

  return (
    <Drawer
      anchor="right"
      open={Boolean(number)}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 520 }, p: 3 } }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: "#0F172A" }}>
            Number details
          </Typography>
          <Typography sx={{ color: "#64748B", fontSize: "0.84rem" }}>
            {number?.phone_number ?? ""}
          </Typography>
        </Box>
        <IconButton aria-label="Close details" onClick={onClose}>
          <CloseRoundedIcon />
        </IconButton>
      </Box>

      {number && (
        <Stack spacing={2.25}>
          {number.safe_error && <Alert severity="warning">{number.safe_error}</Alert>}

          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "0.82rem", color: "#0F172A", mb: 1 }}>
              Operational status
            </Typography>
            <Stack spacing={1}>
              {row("Inventory status", number.status)}
              {row("Vapi connection", number.vapi_connection_status)}
              {row("Routing status", number.routing_status ?? "unknown")}
              {row("Routing target", number.routing_target_url)}
              {row("Assigned business", number.assigned_business_name ?? number.assigned_business_id)}
              {row("Country / region", [number.country_code, number.region].filter(Boolean).join(" / ") || null)}
              {row("Cooldown until", formatDate(number.cooldown_until))}
              {row("Last synced", formatDate(number.last_synced_at))}
              {row("Last routing check", formatDate(number.last_routing_check_at))}
              {row("Last routing update", formatDate(number.last_routing_update_at))}
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "0.82rem", color: "#0F172A", mb: 1 }}>
              Technical details
            </Typography>
            <Stack spacing={1}>
              {row("Provider source", number.provider)}
              {row("Twilio SID", number.twilio_sid)}
              {row("Vapi phone ID", number.vapi_phone_number_id)}
              {row("Voice number ID", number.voice_phone_number_id)}
              {row("Provider setting ID", number.assigned_voice_provider_setting_id)}
              {row(
                "Previous routing",
                number.previous_routing && Object.keys(number.previous_routing).length > 0
                  ? "stored"
                  : null
              )}
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "0.82rem", color: "#0F172A", mb: 1 }}>
              History
            </Typography>
            {isLoading && <CircularProgress size={22} sx={{ color: "#C9734F" }} />}
            {!isLoading && history?.assignments.length === 0 && history.events.length === 0 && (
              <Typography sx={{ fontSize: "0.82rem", color: "#94A3B8" }}>
                No history yet.
              </Typography>
            )}
            <Stack spacing={1.5}>
              {history?.assignments.map((assignment) => (
                <Box key={`${assignment.business_id}-${assignment.assigned_at}`} sx={{ p: 1.5, border: "1px solid #E2E8F0", borderRadius: "10px" }}>
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#0F172A" }}>
                    {assignment.business_name ?? assignment.business_id}
                  </Typography>
                  <Typography sx={{ fontSize: "0.72rem", color: "#64748B" }}>
                    Assigned {formatDate(assignment.assigned_at)}
                  </Typography>
                  {assignment.released_at && (
                    <Typography sx={{ fontSize: "0.72rem", color: "#64748B" }}>
                      Released {formatDate(assignment.released_at)} · {assignment.release_reason ?? "No reason"}
                    </Typography>
                  )}
                </Box>
              ))}
              {history?.events.map((event) => (
                <Box key={`${event.event_type}-${event.created_at}`} sx={{ p: 1.5, border: "1px solid #F1F5F9", borderRadius: "10px" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    <Chip label={event.event_type} size="small" sx={{ fontSize: "0.68rem", backgroundColor: "#F8FAFC", color: "#475569" }} />
                    <Typography sx={{ fontSize: "0.72rem", color: "#94A3B8" }}>
                      {formatDate(event.created_at)}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: "0.78rem", color: "#475569" }}>
                    {event.message ?? "No message"}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        </Stack>
      )}
    </Drawer>
  );
}
